import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { Check, Copy, Gift } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { bindReferral } from '@/lib/api'
import {
  buildReferralLink,
  clearInboundRefCode,
  readInboundRefCode,
} from '@/lib/referral-link'
import { cn } from '@/lib/cn'
import { Button } from '@/ui/Button'

/**
 * The first-time referral prompt.
 *
 * This prompt has no close, escape, backdrop, or skip path. It disappears only after
 * the server validates and durably binds another wallet's referral code.
 *
 * ── Why the error is inline and the field stays filled ──
 * The likely failure is a typo in a code someone was given verbally. Clearing the input
 * on rejection would make them ask their friend again; keeping it lets them fix one
 * character.
 */
export function ReferralPrompt({
  open,
 ownCode,
 onSettled,
}: {
  open: boolean
  /** The user's own code, shown so they can start sharing immediately. */
  ownCode: string | null
  /**
   * Called once the server has durably bound the referral code supplied by the user.
   */
  onSettled: () => void
}) {
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [copied, setCopied] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const reduceMotion = useReducedMotion()

  /** The link this user shares. Built from the current origin — see referral-link.ts. */
  const referralLink = ownCode ? buildReferralLink(ownCode) : ''

  useEffect(() => {
    if (!open) return
    setError(null)

    /*
     * Prefill from the link they arrived on, if there was one. This is the entire point
     * of a referral link: the invited user should not have to retype a code they were
     * already given — most of them would not, and the invite would go unattributed.
     */
    const inbound = readInboundRefCode()
    if (inbound) setCode(inbound)

    // Focus after the mount frame so the entrance animation does not fight it.
    const id = requestAnimationFrame(() => inputRef.current?.focus())
    return () => cancelAnimationFrame(id)
  }, [open])

  const submit = async (value: string) => {
    setBusy(true)
    setError(null)
    try {
      await bindReferral(value)
      // Spent, either way. Leaving it would prefill the prompt for the next account
      // signed in from this browser with a code that was not meant for them.
      clearInboundRefCode()
      onSettled()
    } catch (err) {
      setError(friendlyError(err as Error))
    } finally {
      setBusy(false)
    }
  }

  const copyLink = async () => {
    if (!referralLink) return
    try {
      await navigator.clipboard.writeText(referralLink)
      setCopied(true)
      setTimeout(() => setCopied(false), 1600)
    } catch {
      // Clipboard is permission-gated in some browsers; the link is visible anyway.
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 grid place-items-center bg-canvas/80 p-3 backdrop-blur-md sm:p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduceMotion ? 0 : 0.15 }}
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="referral-title"
            className="surface-glass max-h-[calc(100dvh-1.5rem)] w-full max-w-md overflow-y-auto rounded-[18px] p-4 text-ink ring-1 ring-line shadow-[var(--lift)] sm:max-h-[calc(100dvh-2rem)] sm:p-6"
            initial={reduceMotion ? false : { y: 12, scale: 0.98 }}
            animate={{ y: 0, scale: 1 }}
            exit={reduceMotion ? undefined : { y: 8, scale: 0.99, opacity: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.2, ease: [0.2, 0.8, 0.2, 1] }}
          >
            <div className="flex items-start gap-4">
              <div className="flex items-center gap-2.5">
                <span
                  className="grid size-10 place-items-center rounded-[10px] text-accent-ink"
                  style={{ background: 'var(--m-enamel)', boxShadow: 'var(--btn-primary-shadow)' }}
                >
                  <Gift className="size-4.5" aria-hidden />
                </span>
                <div>
                  <h2 id="referral-title" className="display text-xl text-ink">
                    Referral required
                  </h2>
                  <p className="mt-0.5 text-xs text-muted">
                    Enter another user's referral code to unlock the Ninja dashboard.
                  </p>
                </div>
              </div>
            </div>

            <form
              className="mt-5"
              onSubmit={(e) => {
                e.preventDefault()
                if (code.trim()) void submit(code.trim())
              }}
            >
              <label htmlFor="referral-code" className="sr-only">
                Referral code
              </label>
              <input
                id="referral-code"
                ref={inputRef}
                value={code}
                /*
                 * Upper-cased as they type. The server normalises anyway, but showing
                 * the canonical form means the code on screen matches the code on the
                 * screenshot they were sent.
                 */
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="e.g. 94RJNZT5"
                autoComplete="off"
                spellCheck={false}
                maxLength={32}
                aria-invalid={error !== null}
                aria-describedby={error ? 'referral-error' : undefined}
                className={cn(
                  'surface-glass-2 w-full rounded-[10px] px-3.5 py-3 font-mono text-sm tracking-[0.12em] text-ink ring-1',
                  'placeholder:font-sans placeholder:tracking-normal placeholder:text-faint',
                  'outline-none transition focus:ring-2 focus:ring-accent-edge',
                  error ? 'ring-danger' : 'ring-line',
                )}
              />

              {error && (
                <p id="referral-error" role="alert" className="mt-2 text-xs text-danger">
                  {error}
                </p>
              )}

              <div className="mt-4">
                <Button type="submit" disabled={!code.trim()} loading={busy} fullWidth size="lg">
                  {busy ? 'Validating…' : 'Validate and unlock'}
                </Button>
              </div>
            </form>

            {ownCode && (
              <div className="mt-5 border-t border-line pt-4">
                <p className="label">
                  Referral link
                </p>
                <div className="surface-glass-2 mt-1.5 flex items-center gap-2 rounded-[10px] px-3 py-2.5 ring-1 ring-line">
                  {/*
                    Truncated with an ellipsis rather than wrapped. The link is long and
                    the user does not read it — they copy it. Wrapping would push the
                    copy button below the fold on a narrow screen, which is the one
                    control that matters here.

                    `title` carries the full URL for anyone who does want to read it.
                  */}
                  <span
                    className="min-w-0 flex-1 truncate font-mono text-xs text-muted"
                    title={referralLink}
                  >
                    {referralLink}
                  </span>
                  <button
                    type="button"
                    onClick={() => void copyLink()}
                    aria-label="Copy referral link"
                    className="shrink-0 rounded-[7px] p-1 text-muted transition hover:bg-accent-wash hover:text-ink"
                  >
                    {copied ? (
                      <Check className="size-4 text-accent" aria-hidden />
                    ) : (
                      <Copy className="size-4" aria-hidden />
                    )}
                  </button>
                </div>
                <p className="mt-1.5 text-[11px] text-faint">
                  Your code is{' '}
                  <code className="font-mono tracking-[0.1em] text-accent">{ownCode}</code>
                  {' — anyone opening this link gets it filled in automatically.'}
                </p>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

/**
 * The server's messages are already written for users — a mistyped code says so in
 * plain language. Only the network case needs translating, because "Failed to fetch"
 * describes the transport rather than anything the person can act on.
 */
function friendlyError(err: Error): string {
  if (/failed to fetch|networkerror|load failed/i.test(err.message)) {
    return 'Could not reach the server. Check your connection and try again.'
  }
  return err.message || 'Something went wrong. Try again.'
}
